import { useContext, useEffect, useRef, useState } from 'react';
import {
  AgxHeader,
  AgxButton,
  AgxRow,
  AgxColumn,
  useWindowSize,
  Breakpoints,
  AgxAgentAlert,
  FormConfigType,
  MarketingPackageStage,
  CampaignDetailModel,
  AustralianState,
} from '@urbanx/agx-ui-components';
import dayjs from 'dayjs';
import { useAppSelector } from 'hooks/useAppSelector';
import { AuthContext } from '@urbanx/agx.auth.client';
import { beginCampaignForm } from 'Api/Campaigns/campaignsApi';
import { getMarketingPackages } from 'Api/Marketing/marketingApi';
import { useFormLoader } from 'hooks/useFormLoader';
import { useFeatureToggles } from 'hooks/useFeatureToggles';
import { cleanTwoLineAddress } from 'utils/formatAddress';
import { setAndShowErrorToast } from 'store/config';
import FormDetailsHeader from '../Common/FormDetailsHeader';
import SegmentedPicker from '../../../../SegmentedPicker/SegmentedPicker';
import ListingDetails, { ListingDetailsHandle } from './Common/ListingDetails';
import ContractDetails from './Common/ContractDetails';
import { MarketingPackageModel } from 'Api/Marketing/Types/Marketing';
import MarketingPackageSigningPanel from './MarketingPackage/MarketingPackageSigningPanel';
import { useGetFileLink } from 'components/ui-components/File/fileApi';
import { openFileInNewTab } from 'utils/openFileInNewTab';
import ResumeMarketingPackage from './MarketingPackage/ResumeMarketingPackage';
import MarketingPackageCompletePanel from './MarketingPackage/MarketingPackageCompletePanel';
import type { Segment } from '@urbanx/agx-ui-components';
import { Tabs, ListingTabs } from 'types/Tabs';
import './ListingInfoPanel.scss';
import { FormType } from '@urbanx/agx.campaigns.client';
import LaunchDetails from './Common/LaunchDetails';

interface ListingInfoPanelProps {
  campaign: CampaignDetailModel;
  currentTab: Tabs;
  mobile?: boolean;
}

const ListingInfoPanel = ({
  campaign,
  currentTab,
  mobile = false,
}: ListingInfoPanelProps) => {
  const listingDetailsRef = useRef<ListingDetailsHandle>(null);
  const screenSize = useWindowSize();
  const [selectedTab, setSelectedTab] = useState(ListingTabs.Contracts);
  const [disableButtons, setDisableButtons] = useState(false);
  const { getAuthToken } = useContext(AuthContext);
  const { loadForm } = useFormLoader();
  const [marketingPackages, setMarketingPackages] = useState<
    MarketingPackageModel[]
  >([]);
  const [selectedMarketingPackage, setSelectedMarketingPackage] = useState<
    MarketingPackageModel | undefined
  >();
  const availableForms = useAppSelector(state => state.forms.availableForms);
  const { addMarketingToLiveListing, enableLaunchPad } = useFeatureToggles(
    campaign?.state
  );
  const isNsw = campaign?.state === AustralianState.NSW;

  const isArchived = !!campaign.completionState?.completionType;

  const { address, campaignId, blankContractIsLive, lastUpdated } = campaign;

  const [addressLineOne, addressLineTwo] = cleanTwoLineAddress(address);

  useEffect(() => {
    if (addMarketingToLiveListing) fetchMarketingPackages();
  }, [addMarketingToLiveListing, lastUpdated]);

  const fetchMarketingPackages = async () => {
    const authToken = await getAuthToken();
    if (!authToken) return null;
    const data = await getMarketingPackages(authToken, campaign.campaignId);
    setMarketingPackages(data ?? []);

    if (selectedMarketingPackage) {
      setSelectedMarketingPackage(
        data.find(mp => mp.id === selectedMarketingPackage.id)
      );
    }
  };

  const segments = [
    {
      title: 'Contracts',
      isSelected: selectedTab === ListingTabs.Contracts,
      onSelect: () => setSelectedTab(ListingTabs.Contracts),
    } as Segment,
    {
      title: 'Listing Details',
      isSelected: selectedTab === ListingTabs.ListingDetails,
      onSelect: () => setSelectedTab(ListingTabs.ListingDetails),
    } as Segment,
    ...(enableLaunchPad
      ? [
          {
            title: 'Launch',
            isSelected: selectedTab === ListingTabs.Launch,
            onSelect: () => setSelectedTab(ListingTabs.Launch),
          } as Segment,
        ]
      : []),
  ] as Segment[];

  const lastUpdatedOn = `Last updated ${dayjs(lastUpdated).format('DD/MM/YY')}`;

  const beginContract = async () => {
    if (!getAuthToken) return;

    const contractRequestForm = availableForms.find(
      form => form.type === FormType.ContractRequest
    );

    if (!contractRequestForm) return;

    try {
      setDisableButtons(true);

      const authToken = await getAuthToken();

      if (!authToken) {
        setDisableButtons(false);
        return;
      }

      const newFormId = await beginCampaignForm(
        authToken,
        campaignId,
        contractRequestForm.type,
        contractRequestForm.state
      );

      if (!newFormId) {
        setAndShowErrorToast('Failed to start Contract submission');
        setDisableButtons(false);
        return;
      }

      await loadForm(campaignId, newFormId, FormConfigType.Campaign);
    } catch (err) {
      console.error(err);
    }

    setDisableButtons(false);
  };

  const beginSubmission = async () => {
    if (!getAuthToken) return;

    const contractSubmissionForm = availableForms.find(
      form => form.type === FormType.ContractSubmission
    );

    if (!contractSubmissionForm) return;

    try {
      setDisableButtons(true);

      const authToken = await getAuthToken();

      if (!authToken) {
        setDisableButtons(false);
        return;
      }

      const newFormId = await beginCampaignForm(
        authToken,
        campaignId,
        contractSubmissionForm.type,
        contractSubmissionForm.state
      );

      if (!newFormId) {
        setAndShowErrorToast('Failed to start Contract submission');
        setDisableButtons(false);
        return;
      }

      await loadForm(campaignId, newFormId, FormConfigType.Campaign);
    } catch (err) {
      console.error(err);
    }

    setDisableButtons(false);
  };

  if (selectedMarketingPackage) {
    switch (selectedMarketingPackage.stage?.value) {
      case MarketingPackageStage.Draft:
      case MarketingPackageStage.SubmissionIncomplete:
        return (
          <ResumeMarketingPackage
            onBack={() => setSelectedMarketingPackage(undefined)}
            campaign={campaign}
            marketingPackage={selectedMarketingPackage}
            addressLineOne={addressLineOne}
            addressLineTwo={addressLineTwo}
          />
        );
      case MarketingPackageStage.VendorSigning:
        return (
          <MarketingPackageSigningPanel
            onBack={() => setSelectedMarketingPackage(undefined)}
            marketingPackage={selectedMarketingPackage}
            addressLineOne={addressLineOne}
            addressLineTwo={addressLineTwo}
            campaign={campaign}
          />
        );
      case MarketingPackageStage.Complete:
        return (
          <MarketingPackageCompletePanel
            onBack={() => setSelectedMarketingPackage(undefined)}
            marketingPackage={selectedMarketingPackage}
            addressLineOne={addressLineOne}
            addressLineTwo={addressLineTwo}
          />
        );
      default:
        break;
    }
  }

  return (
    <>
      <AgxColumn centered extraClasses="listingInfoPanel">
        <FormDetailsHeader
          id="listing-info-panel-header"
          formName="Listing"
          detail={lastUpdatedOn}
          currentTab={currentTab}
          onBack={undefined}
        />
        <AgxColumn fill centered extraClasses="contractPanelTitle">
          <AgxHeader
            size={2}
            centered
            dateTestId="agx-listing-info-panel-address"
          >
            {addressLineOne},
            <br />
            {addressLineTwo}
          </AgxHeader>
        </AgxColumn>

        <AgxRow fill justifyCenter>
          <AgxRow extraClasses={'listingSegmentedPickerContainer'}>
            <SegmentedPicker segments={segments} id={'agx-listing-segments'} />
          </AgxRow>
        </AgxRow>

        {selectedTab === ListingTabs.Contracts && (
          <AgxRow fill justifyCenter>
            <AgxColumn extraClasses={'listingSegmentedPickerContainer'}>
              <ContractDetails
                campaign={campaign}
                property={address}
                contractDetails={campaign.contractDetails}
                completionState={campaign.completionState}
                propertyImageUrls={{
                  thumbnail: campaign.propertyImageUrls.thumbnail,
                }}
                mobile={mobile}
              />
            </AgxColumn>
          </AgxRow>
        )}

        {selectedTab === ListingTabs.ListingDetails && (
          <ListingDetails
            ref={listingDetailsRef}
            campaign={campaign}
            mobile={mobile}
            marketingPackages={marketingPackages}
            setSelectedMarketingPackage={setSelectedMarketingPackage}
          />
        )}
        {selectedTab === ListingTabs.Launch && (
          <AgxRow fill justifyCenter>
            <AgxColumn extraClasses={'listingSegmentedPickerContainer'}>
              <LaunchDetails campaign={campaign} />
            </AgxColumn>
          </AgxRow>
        )}
      </AgxColumn>

      {(!isNsw &&
        !campaign.blankContractIsLive &&
        campaign.contractDetails?.length > 0) ||
      (isNsw && !campaign.areVendorsValid) ? (
        <AgxAgentAlert
          extraClasses="offlineContractAlert"
          heading="New Contract generation is disabled."
          body={
            screenSize === Breakpoints.Mobile
              ? 'Contact Support'
              : 'Contact your administrator'
          }
        />
      ) : null}

      {selectedTab === ListingTabs.Contracts && (
        <div className="campaignModalButtons">
          <AgxButton
            dataTestId="agx-submit-contract"
            hollow
            large
            wide={mobile}
            text="Submit Contract"
            shrinkOnLargerDevices
            onClick={beginSubmission}
            disabled={
              (isNsw && !campaign.areVendorsValid) ||
              disableButtons ||
              isArchived
            }
          />
          <AgxButton
            dataTestId="agx-begin-contract"
            primary
            large
            wide={mobile}
            text="New Contract"
            shrinkOnLargerDevices
            onClick={beginContract}
            disabled={
              (!isNsw && !blankContractIsLive) ||
              (isNsw && !campaign.areVendorsValid) ||
              disableButtons ||
              isArchived
            }
          />
        </div>
      )}
      {selectedTab === ListingTabs.ListingDetails && mobile && (
        <div className="campaignModalButtons">
          <AgxButton
            id="agx-infoPanel-addMarketingButton"
            primary
            text={`Add Marketing`}
            large
            wide={mobile}
            onClick={() => listingDetailsRef.current?.addMarketing()}
          />
        </div>
      )}
      {selectedTab === ListingTabs.Launch && (
        <div className="campaignModalButtons">
          <AgxButton
            id="agx-infoPanel-reviewAndLaunchButton"
            hollow
            text="Review & Launch"
            large
            wide={mobile}
            onClick={() => {}}
          />
        </div>
      )}
    </>
  );
};

export default ListingInfoPanel;
